import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, MagnifyingGlass, List, X, Lightning, SignOut, User } from "@phosphor-icons/react";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Shop" },
  { to: "/integration-guide", label: "API Guide" },
];

export const Header = () => {
  const { count, setIsOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/products?search=${encodeURIComponent(search)}`);
    setMobileOpen(false);
  };

  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  const isLoggedIn = user && user !== false;

  return (
    <header
      className="sticky top-0 z-50 w-full bg-white/85 backdrop-blur-xl border-b border-zinc-200"
      data-testid="site-header"
    >
      {/* Top promo strip */}
      <div className="bg-zinc-950 text-white text-xs font-mono uppercase tracking-[0.2em] py-2 px-4 text-center">
        <Lightning size={12} weight="fill" className="inline mr-1" /> SHOCK SALE LIVE
        — FREE SHIPPING ON ORDERS OVER $99
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link
          to="/"
          className="flex items-center gap-2 font-heading font-black text-2xl"
          data-testid="logo-link"
        >
          <span className="bg-zinc-950 text-white px-2 py-0.5">NEO</span>
          <span className="text-zinc-950">TECH</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(" ", "-")}`}
              className="px-4 py-2 text-sm font-semibold uppercase tracking-wider hover:bg-zinc-100 transition-colors"
            >
              {n.label}
            </Link>
          ))}
          {isLoggedIn ? (
            <Link
              to="/admin"
              data-testid="nav-link-admin"
              className="px-4 py-2 text-sm font-semibold uppercase tracking-wider hover:bg-zinc-100 transition-colors text-[#002FA7]"
            >
              Admin
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-link-login"
              className="px-4 py-2 text-sm font-semibold uppercase tracking-wider hover:bg-zinc-100 transition-colors"
            >
              Sign in
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={onSearch} className="hidden md:flex relative">
            <Input
              type="text"
              data-testid="header-search-input"
              placeholder="Search GPUs, CPUs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-56 rounded-none border-zinc-300 bg-zinc-50 focus-visible:ring-zinc-950 pr-10 font-mono text-sm"
            />
            <button
              type="submit"
              className="absolute right-0 top-0 h-full px-3 hover:bg-zinc-100"
              data-testid="header-search-submit"
            >
              <MagnifyingGlass size={18} weight="bold" />
            </button>
          </form>

          <Button
            data-testid="cart-toggle-button"
            variant="outline"
            onClick={() => setIsOpen(true)}
            className="rounded-none border-zinc-950 hover:bg-zinc-950 hover:text-white relative font-bold"
          >
            <ShoppingCart size={18} weight="bold" />
            <span className="hidden sm:inline ml-2">CART</span>
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-[#FF2A00] text-white text-xs font-mono font-bold w-5 h-5 flex items-center justify-center"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </Button>

          {isLoggedIn && (
            <Button
              variant="outline"
              onClick={onLogout}
              data-testid="header-logout-button"
              className="rounded-none border-zinc-950 hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00] font-bold hidden sm:inline-flex"
              title={`Signed in as ${user.email}`}
            >
              <SignOut size={16} weight="bold" />
            </Button>
          )}

          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-zinc-200 bg-white px-4 py-4 flex flex-col gap-2">
          <form onSubmit={onSearch} className="flex gap-2 mb-2">
            <Input
              type="text"
              data-testid="mobile-search-input"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-none flex-1"
            />
            <Button
              type="submit"
              className="rounded-none bg-zinc-950 hover:bg-zinc-800"
              data-testid="mobile-search-submit"
            >
              <MagnifyingGlass size={16} weight="bold" />
            </Button>
          </form>
          {NAV_LINKS.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setMobileOpen(false)}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className="px-3 py-3 text-base font-semibold uppercase border-b border-zinc-100"
            >
              {n.label}
            </Link>
          ))}
          {isLoggedIn ? (
            <>
              <Link
                to="/admin"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-admin"
                className="px-3 py-3 text-base font-semibold uppercase border-b border-zinc-100 text-[#002FA7]"
              >
                Admin
              </Link>
              <button
                onClick={() => {
                  setMobileOpen(false);
                  onLogout();
                }}
                data-testid="mobile-logout-button"
                className="px-3 py-3 text-base font-semibold uppercase border-b border-zinc-100 text-left text-[#FF2A00]"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-nav-login"
              className="px-3 py-3 text-base font-semibold uppercase border-b border-zinc-100"
            >
              Sign in
            </Link>
          )}
        </div>
      )}
    </header>
  );
};
